import _ from 'lodash';
import BigNumber from 'bignumber.js';
import { FormatUtility } from '@codequality/vue-cli-base';

import { E0円非表示フラグ, Eフェーズ種別 } from '@/openapi/enum';
import { Cデータ実態BSItem, Cデータ実態BSValue, データListItemResponse, 不動産価格 } from '@/openapi/user';

import 決算期Utility, { 決算期TableHeader } from '@/classes/common/決算期Utility';
import AbstractPlbscfService, {
  PlbscfYearValue,
  PlbscfTypeList,
  CalcResultMap,
  PrintTemplateConfigList,
} from '@/classes/service/AbstractPlbscfService';
import AbstractデータService, { ServiceType, 帳票データ } from '@/classes/service/AbstractデータService';
import 分析PlbscfService from '@/classes/service/分析/分析PlbscfService';
import { 保有不動産 } from '@/classes/service/分析/保有不動産Service';
import 法人保有不動産Service from '@/classes/service/分析/法人保有不動産Service';
import 償却Service from '@/classes/service/分析計画/償却Service';
import { 償却 } from '@/classes/service/分析計画/償却Type';
import { CALC_PROCESS_MAP_貸借対照表, PlbscfListItemConfig, PlbscfType } from '@/master/Plbscf';

// ------------------------------------------------------------------
// type
// ------------------------------------------------------------------

/**
 * parse の返却値
 */
export type 実態BS = {
  データpk?: number;
  header: 決算期TableHeader;
  list: 実態BSListItem[]; // size：行数
};
/**
 * 一行の情報
 */
export type 実態BSListItem = {
  config: PlbscfListItemConfig; // 色とかラベルの情報
  value: 実態BSListValueItem;
};
/**
 * 一行のデータ情報
 */
export type 実態BSListValueItem = {
  item: Cデータ実態BSItem;
  plbscfYearValue: PlbscfYearValue;
  残高比率Group: {
    A: 残高比率;
    B: 残高比率;
  };
};
type 残高比率 = {
  実態残高: number;
  総資産比率?: number;
};

// ------------------------------------------------------------------
// const
// ------------------------------------------------------------------

const 資産の部合計_LABEL = '資産の部　合計';
const 差異_LABEL = '差異';

export default class 実態BSService extends AbstractPlbscfService<実態BS> {
  protected serviceType = ServiceType.実態BS;

  private 分析PlbscfService: 分析PlbscfService | null = null;

  private 保有不動産Service: 法人保有不動産Service | null = null;

  private 償却Service: 償却Service | null = null;

  private 保有不動産: 保有不動産 | null = null;

  private 償却: 償却 | null = null;

  /** parseで渡された cデータ */
  private cデータ実態BSItems: Cデータ実態BSItem[] = [];

  /** 計算格納用データ */
  private calc実態BSListItems: 実態BSListItem[] = [];

  // ------------------------------------------------------------------
  // constructor
  // ------------------------------------------------------------------

  public constructor(eフェーズ種別: Eフェーズ種別 | null = null) {
    super(eフェーズ種別);
  }

  // ------------------------------------------------------------------
  // getter/setter
  // ------------------------------------------------------------------

  /**
   * parseで渡された cデータ
   * @returns
   */
  public get getCデータ実態BSItems(): Cデータ実態BSItem[] {
    return this.cデータ実態BSItems;
  }

  // ------------------------------------------------------------------
  // override
  // ------------------------------------------------------------------

  /**
   * @override
   * @param データs
   * @returns
   */
  public parse(
    データs: データListItemResponse[],
    option?: Record<string, AbstractデータService<unknown> | true>
  ): 実態BS {
    // 必要データに絞り込んで計算可能データ化
    const filteredデータs = this.フェーズ種別Filter(this.入力分類Filter(データs));
    // 各service 初期化
    if (this.分析PlbscfService === null) {
      this.分析PlbscfService =
        option?.分析PlbscfService === undefined
          ? new 分析PlbscfService(this.eフェーズ種別)
          : (option?.分析PlbscfService as 分析PlbscfService);
      this.分析PlbscfService.set勘定科目s = this.勘定科目s;
    }
    if (this.保有不動産Service === null) {
      this.保有不動産Service =
        option?.保有不動産Service === undefined
          ? new 法人保有不動産Service(this.eフェーズ種別)
          : (option?.保有不動産Service as 法人保有不動産Service);
    }
    if (this.償却Service === null) {
      this.償却Service =
        option?.償却Service === undefined
          ? new 償却Service(
              this.eフェーズ種別 === Eフェーズ種別.分析フェーズ
                ? Eフェーズ種別.分析フェーズ
                : Eフェーズ種別.計画フェーズ
            )
          : (option?.償却Service as 償却Service);
      this.償却Service.set勘定科目s = this.勘定科目s;
    }

    // 保有不動産、償却、貸借対照表の情報をparseで取得
    this.保有不動産 = this.保有不動産Service.parse(データs);
    this.償却 = this.償却Service.parse(データs, { functionType: '償却不足額' });
    const plbscfTypeList: PlbscfTypeList = this.分析PlbscfService
      .parse(データs, { isNoFilter: true })
      .find((p) => p.type === PlbscfType.貸借対照表)!;

    // JSON.parseしDB保存用データを生成
    const 実態BSデータ = filteredデータs.length === 0 ? undefined : filteredデータs[0];
    this.cデータ実態BSItems =
      実態BSデータ === undefined ? [] : (JSON.parse(実態BSデータ.cデータ) as Cデータ実態BSItem[]);

    // 前期（最終列）のデータを取得する
    const colIndex = 決算期Utility.過去決算期数 - 1;

    // 実態BSの画面表示用データを、貸借対照表の行数分作成
    this.calc実態BSListItems = plbscfTypeList.list.map((plbscfListItem) => {
      if (plbscfListItem.config.label.name === '資産の部・負債及び純資産の部【差異】')
        plbscfListItem.config.label.name = 差異_LABEL;
      const yearValue = plbscfListItem.yearValues[colIndex];
      // 現在の行のデータがDB保存用データに存在するか（勘定科目行のみ）
      let foundItem = this.cデータ実態BSItems.find(
        (item) => yearValue.item.勘定科目Pk !== undefined && yearValue.item.勘定科目Pk === item.勘定科目Pk
      );
      if (foundItem === undefined) {
        // DB保存用データに存在しない場合
        foundItem = {
          勘定科目Pk: yearValue.item.勘定科目Pk,
          修正事項: {
            name: '',
            value: 0,
          },
          中小企業特性の反映: {
            name: '',
            value: 0,
          },
        };
        // 勘定科目行 かつ yearValue.item.value が存在する場合、DB保存用データに追加
        if (yearValue.item.勘定科目Pk !== undefined && yearValue.item.value !== undefined) {
          this.cデータ実態BSItems.push(foundItem);
        }
      }
      if (plbscfListItem.config.label.name === 差異_LABEL) {
        foundItem.修正事項.name = 差異_LABEL;
        foundItem.中小企業特性の反映.name = 差異_LABEL;
      }
      return {
        config: plbscfListItem.config,
        value: {
          item: foundItem!,
          plbscfYearValue: yearValue,
          残高比率Group: {
            A: {
              実態残高: 0,
              総資産比率: 0,
            },
            B: {
              実態残高: 0,
              総資産比率: 0,
            },
          },
        },
      };
    });
    // 初回起動時、初期値を格納
    if (実態BSデータ === undefined) this.initialize実態BSListItems();
    this.setCalcValue();
    return {
      データpk: 実態BSデータ?.pk,
      header: plbscfTypeList.header[colIndex],
      list: this.create実態BSList(option?.isNoFilter as true),
    };
  }

  /**
   * @override
   * @param データs
   * @returns
   */
  public parse帳票ListItemRequest(
    データs: データListItemResponse[],
    option?: Record<string, AbstractデータService<unknown>>
  ): 帳票データ {
    const 実態BS = this.parse(データs, option);
    // テンプレート行設定
    const templateConfigList: PrintTemplateConfigList = {
      header: 0,
      subheader: 1,
      row: {
        body: 2,
        specialHeader: 3,
        header: 4,
        subHeader: 5,
        itemFirst: 6,
        itemSecond: 7,
        diff: 8,
      },
    };

    // 以下帳票データ整形
    // 初行設定込みで初期化
    const templateRows: number[] = [templateConfigList.header, templateConfigList.subheader!];
    const initializedHeader = new Array(8).fill(null);

    const values: unknown[][] = [
      [AbstractデータService.getMoneyUnitLabel(), '', 実態BS.header.label].concat(initializedHeader),
      [],
    ];

    // 行でループ
    実態BS.list.forEach((実態BSListItem) => {
      const config = 実態BSListItem.config;
      const value = 実態BSListItem.value;
      templateRows.push(this.getTemplateConfig(templateConfigList, config));

      const labels: (string | number)[] = [];
      if (config.label.hierarchy === 0) {
        labels.push(config.label.name);
        labels.push('');
      } else {
        labels.push('');
        labels.push(config.label.name);
      }
      const datas: (string | number)[] = [
        this.getFormatValueByType(config, value.plbscfYearValue),
        value.item.修正事項.name,
        this.getMoneyUnitValue(value.item.修正事項.value),
        this.getMoneyUnitValue(value.残高比率Group.A.実態残高),
        FormatUtility.percent(value.残高比率Group.A.総資産比率),
        value.item.中小企業特性の反映.name,
        this.getMoneyUnitValue(value.item.中小企業特性の反映.value),
        this.getMoneyUnitValue(value.残高比率Group.B.実態残高),
        FormatUtility.percent(value.残高比率Group.B.総資産比率),
      ];

      // 一行追加
      values.push(labels.concat(datas));
    });
    return {
      templateRows,
      values,
    };
  }

  // ------------------------------------------------------------------
  // public methods
  // ------------------------------------------------------------------

  /**
   * 初期値、計算結果を設定
   */
  public initializeAndSetCalcValue(): void {
    this.initialize実態BSListItems();
    this.setCalcValue();
  }

  /**
   * 計算結果を設定
   */
  public setCalcValue(): void {
    const calcResultMap: CalcResultMap = _.cloneDeep(CALC_PROCESS_MAP_貸借対照表);
    const columnCount = 2;

    // 合計行：「修正金額」を計算
    const calcValues = this.calc実態BSListItems.map((listItem) => ({
      勘定科目Pk: listItem.value.item.勘定科目Pk,
      values: [listItem.value.item.修正事項.value, listItem.value.item.中小企業特性の反映.value],
    }));
    for (let index = 0; index < columnCount; index++) {
      const itemValues = calcValues.map((calcValue) => ({
        item: {
          勘定科目Pk: calcValue.勘定科目Pk,
          value: calcValue.values[index],
        },
      }));
      this.setCalcResultMapValue(calcResultMap, itemValues, index);
    }
    // 合計行：「修正金額」の結果を設定
    for (const listItem of this.calc実態BSListItems) {
      const config = listItem.config;
      if (!this.isCodeFilter(config.valueType, config.filter)) continue;
      const code = config.filter.code;
      if (!calcResultMap.has(code)) continue;
      const results = calcResultMap.get(code)!.results!;
      listItem.value.item.修正事項.value = results[0].calcValue!;
      listItem.value.item.中小企業特性の反映.value = results[1].calcValue!;
    }

    let 資産の部合計Item: 実態BSListItem | null = null;
    let 負債及び純資産合計Item: 実態BSListItem | null = null;

    // 「実態残高」を計算し、設定
    for (const listItem of this.calc実態BSListItems) {
      const labelName = listItem.config.label.name;
      const value = listItem.value;
      const yearValue = AbstractPlbscfService.getYearValue(value.plbscfYearValue);
      value.残高比率Group.A.実態残高 = new BigNumber(yearValue).plus(value.item.修正事項.value).toNumber();
      value.残高比率Group.B.実態残高 = new BigNumber(value.残高比率Group.A.実態残高)
        .plus(value.item.中小企業特性の反映.value)
        .toNumber();
      if (labelName === 資産の部合計_LABEL) 資産の部合計Item = listItem;
      else if (labelName === '負債及び純資産　合計') 負債及び純資産合計Item = listItem;
    }

    // 「総資産比率」を計算し、設定
    let is負債及び純資産合計 = false;
    for (const listItem of this.calc実態BSListItems) {
      const labelName = listItem.config.label.name;
      const value = listItem.value;
      if (labelName === 差異_LABEL) {
        value.残高比率Group.A.総資産比率 = undefined;
        value.残高比率Group.B.総資産比率 = undefined;
        continue;
      }
      const denominatorItem = is負債及び純資産合計 ? 負債及び純資産合計Item! : 資産の部合計Item!;
      value.残高比率Group.A.総資産比率 = this.calc総資産比率(
        value.残高比率Group.A.実態残高,
        denominatorItem.value.残高比率Group.A.実態残高
      );
      value.残高比率Group.B.総資産比率 = this.calc総資産比率(
        value.残高比率Group.B.実態残高,
        denominatorItem.value.残高比率Group.B.実態残高
      );
      if (labelName === 資産の部合計_LABEL) is負債及び純資産合計 = true;
    }
  }

  /**
   * 画面表示用データを生成
   *
   * @param isNoFilter
   * @returns
   */
  public create実態BSList(isNoFilter?: true): 実態BSListItem[] {
    if (isNoFilter) return this.calc実態BSListItems;

    // Cデータ実態BSValue に、空文字 または 0でない値が存在するかを返却するローカル関数
    const isNotZero実態BSValue = (bsv: Cデータ実態BSValue): boolean => {
      return bsv.name.length > 0 || bsv.value !== 0;
    };

    return this.calc実態BSListItems.filter((listItem) => {
      if (AbstractデータService.計画書.c分析0円非表示フラグ === E0円非表示フラグ.表示) return true;

      // 0円非表示の対象行か
      if (this.isTargetToFilterNotZero(listItem.config)) return true;

      // plbscfYearValue が0かどうか
      if (this.isNotZeroYearValue(listItem.value.plbscfYearValue)) return true;

      // Cデータ実態BSItem が0または空ではないかどうか
      const valueItem = listItem.value.item;
      if (isNotZero実態BSValue(valueItem.中小企業特性の反映) || isNotZero実態BSValue(valueItem.修正事項)) return true;

      return false;
    });
  }

  // ------------------------------------------------------------------
  // private methods
  // ------------------------------------------------------------------

  /**
   * 実態BSListItemsに初期値を設定
   */
  private initialize実態BSListItems(): void {
    const 表示勘定科目s = this.表示勘定科目s(this.勘定科目s);
    // 実態BSListItemsに値を設定する
    for (const listItem of this.calc実態BSListItems) {
      const item = listItem.value.item;
      // 初期値を設定
      item.修正事項.value = 0;
      item.中小企業特性の反映.value = 0;
      if (listItem.config.label.name !== 差異_LABEL) {
        item.修正事項.name = '';
        item.中小企業特性の反映.name = '';
      }
      // 「有形固定資産」と「無形固定資産」の「修正金額1」については、以下の値を初期値で入れる
      // 「保有不動産情報登録の含み損益」ー「償却不足額の計算の償却不足額」（「保有不動産情報登録」にない科目は「0」として扱う）
      const found勘定科目 = 表示勘定科目s.find((勘) => 勘.pk === item.勘定科目Pk);
      if (
        found勘定科目 !== undefined &&
        found勘定科目.c中分類 !== undefined &&
        ['有形固定資産', '無形固定資産'].includes(found勘定科目.c中分類) &&
        this.保有不動産 !== null &&
        this.償却 !== null
      ) {
        // 「保有不動産情報登録の含み損益」
        const 含み損益 =
          this.保有不動産.total.含み損益[found勘定科目.c勘定科目 as keyof 不動産価格] === undefined
            ? 0
            : this.保有不動産.total.含み損益[found勘定科目.c勘定科目 as keyof 不動産価格]!;

        // 「償却不足額の計算の償却不足額」
        const found科目別償却 = this.償却.total.償却不足額?.科目別集計.償却s.find(
          (科目別償却) => 科目別償却.科目名 === found勘定科目.c勘定科目
        );
        const 償却不足額 = found科目別償却?.償却不足額 === undefined ? 0 : found科目別償却?.償却不足額;

        // 「保有不動産情報登録の含み損益」ー「償却不足額の計算の償却不足額」
        item.修正事項.value = new BigNumber(含み損益).minus(償却不足額).toNumber();
      }
    }
  }

  /**
   * 総資産比率を計算する
   * @param numerator
   * @param denominator
   * @returns
   */
  private calc総資産比率(numerator: number, denominator: number): number {
    return denominator === 0
      ? 0
      : new BigNumber(numerator).div(denominator).times(100).dp(1, BigNumber.ROUND_HALF_UP).toNumber();
  }
}
